import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  TrendingUp,
  People,
  Home,
  Receipt,
  CurrencyRupee,
  Assessment,
  HowToReg,
} from '@mui/icons-material';
import {
  LineChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TabPanel,
  StyledTabs,
  KPICard,
  ChartCard,
  DataTable,
  COLORS,
  CHART_COLORS,
  PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange } from './components/DateRangeFilter';
import useReportData from './hooks/useReportData';
import {
  fetchBusinessHealth,
  fetchTeamsKPI,
  fetchAgentsKPI,
} from '../../services/reportV2Service';

// ─── Formatting helpers ──────────────────────────────────────────────────────

const formatCurrency = (val) => {
  if (val == null) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${val}`;
};

const formatNumber = (val) => {
  if (val == null) return '0';
  return val.toLocaleString('en-IN');
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function ExecutiveDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [businessDateRange, setBusinessDateRange] = useState(getDefaultDateRange());
  const [teamDateRange, setTeamDateRange] = useState(getDefaultDateRange());
  const [agentDateRange, setAgentDateRange] = useState(getDefaultDateRange());

  const {
    data: healthData,
    loading: healthLoading,
    error: healthError,
  } = useReportData(fetchBusinessHealth, businessDateRange, {}, tabValue === 0);

  const {
    data: teamData,
    loading: teamLoading,
    error: teamError,
  } = useReportData(fetchTeamsKPI, teamDateRange, {}, tabValue === 1);

  const {
    data: agentData,
    loading: agentLoading,
    error: agentError,
  } = useReportData(fetchAgentsKPI, agentDateRange, {}, tabValue === 2);

  const handleTabChange = (_, newValue) => setTabValue(newValue);

  // ── Business Health helpers ────────────────────────────────────────────────
  const rawKpis = healthData?.kpis || {};
  const kpi = (key) => rawKpis[key]?.value ?? rawKpis[key] ?? 0;
  const kpiTrend = (key) => rawKpis[key]?.trending;
  const kpiChange = (key) => rawKpis[key]?.change?.value;
  const kpiLabel = (key) => rawKpis[key]?.change?.label;
  const revenueTrend = healthData?.revenue_trend || [];
  const leadsVsConversions = healthData?.leads_vs_conversions || [];

  // ── Teams / Agents data ────────────────────────────────────────────────────
  const teamTableRows = teamData?.table || [];
  const teamChartRows = teamData?.chart || [];
  const agentTableRows = agentData?.table || [];
  const agentChartRows = agentData?.chart || [];

  const TABS = [
    'Business Health Snapshot',
    'Teams KPI Summary',
    'Agents KPI Summary',
  ];

  // ── Shared table column definitions ────────────────────────────────────────
  const teamColumns = [
    { label: 'Team', field: 'team' },
    { label: 'Leads Assigned', field: 'leadsAssigned', align: 'right' },
    { label: 'Leads Contacted', field: 'leadsContacted', align: 'right' },
    { label: 'Leads Connected', field: 'leadsConnected', align: 'right' },
    { label: 'Details Shared (Unique Leads)', field: 'detailsShared', align: 'right' },
    { label: 'Site Visits Done (Unique Leads)', field: 'siteVisits', align: 'right' },
    { label: 'Bookings', field: 'bookings', align: 'right' },
    { label: 'Conversion %', field: 'conversionRate', align: 'right', render: (v) => `${v}%` },
    { label: 'Sales Value', field: 'salesValue', align: 'right', render: (v) => formatCurrency(v) },
    { label: 'Commission', field: 'commissionRevenue', align: 'right', render: (v) => formatCurrency(v) },
  ];

  const agentColumns = [
    { label: 'Agent', field: 'agent' },
    { label: 'Leads Assigned', field: 'leadsAssigned', align: 'right' },
    { label: 'Leads Contacted', field: 'leadsContacted', align: 'right' },
    { label: 'Leads Connected', field: 'leadsConnected', align: 'right' },
    { label: 'Details Shared (Unique Leads)', field: 'detailsShared', align: 'right' },
    { label: 'Site Visits Done (Unique Leads)', field: 'siteVisits', align: 'right' },
    { label: 'Bookings', field: 'bookings', align: 'right' },
    { label: 'Conversion %', field: 'conversionRate', align: 'right', render: (v) => `${v}%` },
    { label: 'Sales Value', field: 'salesValue', align: 'right', render: (v) => formatCurrency(v) },
    { label: 'Commission', field: 'commissionRevenue', align: 'right', render: (v) => formatCurrency(v) },
  ];

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress sx={{ color: COLORS.teal[600] }} />
    </Box>
  );

  const renderEmpty = (label) => (
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      No {label} data available for the selected period.
    </Alert>
  );

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={handleTabChange} tabs={TABS} />

      {/* Tab 1: Business Health Snapshot — API-driven */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Key Performance Indicators
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Current period snapshot
            </Typography>
          </Box>
          <DateRangeFilter value={businessDateRange} onChange={setBusinessDateRange} />
        </Box>

        {healthError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{healthError}</Alert>
        )}

        {healthLoading ? renderLoading() : (
          <>
            <Grid container spacing={3} columns={12} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Sales Value" value={formatCurrency(kpi('total_revenue'))} icon={CurrencyRupee} color={COLORS.teal[600]} trend={kpiTrend('total_revenue')} trendValue={kpiChange('total_revenue')} subtitle={kpiLabel('total_revenue')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Commission Revenue" value={formatCurrency(kpi('commission_revenue'))} icon={CurrencyRupee} color={COLORS.teal[700]} trend={kpiTrend('commission_revenue')} trendValue={kpiChange('commission_revenue')} subtitle={kpiLabel('commission_revenue')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Total Leads" value={formatNumber(kpi('total_leads'))} icon={People} color={COLORS.blue} trend={kpiTrend('total_leads')} trendValue={kpiChange('total_leads')} subtitle={kpiLabel('total_leads')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Qualified Leads" value={formatNumber(kpi('qualified_leads'))} icon={HowToReg} color={COLORS.pink} trend={kpiTrend('qualified_leads')} trendValue={kpiChange('qualified_leads')} subtitle={kpiLabel('qualified_leads')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Active Leads" value={formatNumber(kpi('active_leads'))} icon={Assessment} color={COLORS.indigo} trend={kpiTrend('active_leads')} trendValue={kpiChange('active_leads')} subtitle={kpiLabel('active_leads')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Site Visits" value={formatNumber(kpi('site_visits'))} icon={Home} color={COLORS.orange} trend={kpiTrend('site_visits')} trendValue={kpiChange('site_visits')} subtitle={kpiLabel('site_visits')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Bookings" value={formatNumber(kpi('bookings'))} icon={Receipt} color={COLORS.green} trend={kpiTrend('bookings')} trendValue={kpiChange('bookings')} subtitle={kpiLabel('bookings')} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <KPICard title="Conversion Rate" value={`${kpi('conversion_rate')}%`} icon={TrendingUp} color={COLORS.purple} trend={kpiTrend('conversion_rate')} trendValue={kpiChange('conversion_rate')} subtitle={kpiLabel('conversion_rate')} />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <ChartCard title="Monthly Sales & Commission" subtitle="Last 6 months" height={320}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueTrend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                      <YAxis tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" name="Sales Value (₹)" stroke={COLORS.teal[600]} strokeWidth={2} dot={{ fill: COLORS.teal[600], r: 4 }} />
                      <Line type="monotone" dataKey="commission" name="Commission Revenue (₹)" stroke={COLORS.indigo} strokeWidth={2} dot={{ fill: COLORS.indigo, r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <ChartCard title="Leads vs Conversions by Month" subtitle="Monthly comparison" height={320}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={leadsVsConversions} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="month" tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                      <YAxis tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend />
                      <Bar dataKey="leads" name="Leads" fill={COLORS.teal[500]} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="conversions" name="Conversions" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Tab 2: Team-wide KPI Summary */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Team Performance
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              KPI breakdown by team
            </Typography>
          </Box>
          <DateRangeFilter value={teamDateRange} onChange={setTeamDateRange} />
        </Box>

        {teamError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{teamError}</Alert>
        )}

        {teamLoading ? renderLoading() : teamTableRows.length === 0 ? renderEmpty('team') : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <DataTable columns={teamColumns} rows={teamTableRows} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Team Comparison" subtitle="Leads, Details Shared, Site Visits & Bookings" height={340}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={teamChartRows} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                    <YAxis type="category" dataKey="team" tick={{ fontSize: 12, fill: COLORS.slate[600] }} width={50} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                    <Legend />
                    <Bar dataKey="leads" name="Leads" stackId="a" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="detailsShared" name="Details Shared" stackId="a" fill={CHART_COLORS[1]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="siteVisits" name="Site Visits" stackId="a" fill={CHART_COLORS[2]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="bookings" name="Bookings" stackId="a" fill={CHART_COLORS[3]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Tab 3: Agent-wise KPI Summary */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Agent Performance
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              KPI breakdown by agent
            </Typography>
          </Box>
          <DateRangeFilter value={agentDateRange} onChange={setAgentDateRange} />
        </Box>

        {agentError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{agentError}</Alert>
        )}

        {agentLoading ? renderLoading() : agentTableRows.length === 0 ? renderEmpty('agent') : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <DataTable columns={agentColumns} rows={agentTableRows} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Agent Comparison" subtitle="Leads Contacted, Connected, Details Shared, Site Visits & Bookings" height={340}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={agentChartRows}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 60, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: COLORS.slate[600] }} />
                    <YAxis
                      type="category"
                      dataKey="agent"
                      tick={{ fontSize: 12, fill: COLORS.slate[600] }}
                      width={70}
                    />
                    <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                    <Legend />
                    <Bar dataKey="leadsContacted" name="Leads Contacted" stackId="a" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="leadsConnected" name="Leads Connected" stackId="a" fill={CHART_COLORS[1]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="detailsShared" name="Details Shared" stackId="a" fill={CHART_COLORS[2]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="siteVisits" name="Site Visits" stackId="a" fill={CHART_COLORS[3]} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="bookings" name="Bookings" stackId="a" fill={CHART_COLORS[4]} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>
        )}
      </TabPanel>

    </PageContainer>
  );
}
